<script setup lang="ts">
/**
 * test local url
 * 1. http://localhost:8080/mercari-campaign?slug=test-campaign-a
 * 2. http://localhost:8080/mercari-campaign?slug=mercari-%E4%B8%89%E6%9C%88%E7%8B%82%E6%AD%A1%E7%9B%9B%E5%85%B8
 */
import { ref, onMounted, computed } from 'vue'
import { fetchInappMercariCampaignSlug } from '@/assets/ts/apiManagement'
import MercariCampaignCouponSection from '@/pages/mercari-campaign/components/_MercariCampaignCouponSection.vue'
import MercariCampaignImageSection from '@/pages/mercari-campaign/components/_MercariCampaignImageSection.vue'
import MercariCampaignBlogSection from '@/components/ui/campaignCard/CampaignCardSection.vue'
import MercariTermsAndConditions from '@/components/ui/termsAndConditions/TermsAndConditions.vue'
import MercariCampaignProductListSection from '@/pages/mercari-campaign/components/_MercariCampaignProductListSection.vue'
import {
	createGAEvent,
	GaGeneralEvent,
	GaGeneralPageName,
} from '@/assets/ts/customEvent'
import { getVueAssetImagePath } from '@/lib/utils'
import BaseButton from '@/components/ui/button/BaseButton.vue'
import type { IMercariProductListFetchCompleteEvent } from '@/components/MercariProductList.vue'
import MercariCampaignShopProductListSection from './_MercariCampaignShopProductListSection.vue'
import { isMobile } from '@/legacy/ts/checkDevice'
import type { Blog, Coupon } from '@/types/Mercari'

export type SectionType =
	| 'coupon'
	| 'image'
	| 'mercari_product_list'
	| 'blog'
	| 'text'
	| 'product_list'

interface Section {
	menuTitle: string
	title: string
	subtitle: string
	type: SectionType
	coupons?: Coupon[]
	webImage?: string
	url?: string
	tracking?: string
	keywords?: string[]
	blogs?: Blog[]
	content?: string
	appImage?: string
	tabs?: ShopIdProductListTab[]
}

interface Campaign {
	name: string
	shareUrl: string
	appImage: string
	webImage: string
	sections: Section[]
	metaDescription: string
}

export interface ShopIdProductListTab {
	type: string
	shop_id: string
	label: string
	keyword: string
}

export interface BuyaholicProductListTab extends ShopIdProductListTab {
	warehouses?: string
	interests?: string
	sort?: string
	username?: string
}

const props = defineProps<{
	pageContent: any
}>()

const campaignFirstSlugData = ref<Campaign | null>(null)

const hasServerError = ref(false)

const campaignSlug = computed(() => {
	if (import.meta.env.PROD) {
		return window.location.pathname.split('/').filter(Boolean).pop() || ''
	}
	return new URLSearchParams(window.location.search).get('slug') || ''
})

// for server error case
const refreshPage = () => {
	window.location.reload()

	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: GaGeneralPageName.MercariCampaign,
		button_name: 'Try Again',
	})
}

const handleFetchComplete = (event: IMercariProductListFetchCompleteEvent) => {
	if (!event.success) {
		hasServerError.value = true
	}
}

const isSectionType = (section: Section, type: SectionType): boolean => {
	return section.type === type
}

const { couponCardText } = props.pageContent

const updateMetaData = (campaign: Campaign) => {
	if (!campaign) return

	// rewrite document title in stag/prod env
	// ex: "Campaign | Buy&amp;Ship（香港）"
	// expect: "{api.name} | Buy&amp;Ship（香港）"
	document.title = document.title.replace('Campaign', campaign.name)

	const metaDescription = document.querySelector('meta[name="description"]')
	if (metaDescription) {
		metaDescription.setAttribute('content', campaign.metaDescription)
	}

	const ogTitle = document.querySelector('meta[property="og:title"]')
	if (ogTitle) {
		ogTitle.setAttribute('content', campaign.name)
	}

	const ogDesc = document.querySelector('meta[property="og:description"]')
	if (ogDesc) {
		ogDesc.setAttribute('content', campaign.name)
	}

	const ogImage = document.querySelector('meta[property="og:image"]')
	if (ogImage && campaign.webImage) {
		ogImage.setAttribute('content', campaign.webImage)
	}
}

const fetchCampaignData = async () => {
	try {
		if (!campaignSlug.value) {
			console.error('No campaign slug found')
			hasServerError.value = true
			return
		}

		campaignFirstSlugData.value = await fetchInappMercariCampaignSlug(
			campaignSlug.value,
		)

		updateMetaData(campaignFirstSlugData.value)
	} catch (error) {
		console.error('Failed to fetch campaign data:', error)
		hasServerError.value = true
	}
}

onMounted(() => {
	fetchCampaignData()

	createGAEvent(GaGeneralEvent.PageLoad, {
		page_name: GaGeneralPageName.MercariCampaign,
	})
})
</script>

<template>
	<template v-if="hasServerError">
		<div
			class="mx-auto my-[120px] flex max-w-[300px] flex-col items-center gap-32px mdp:my-[140px]"
		>
			<div class="h-[150px] w-[150px]">
				<img
					class="w-full"
					:src="getVueAssetImagePath('mercari-product-list-server-error.png')"
					alt="mercari product list server-error"
				/>
			</div>

			<div class="flex flex-col items-center gap-16px">
				<div class="flex flex-col gap-12px">
					<p class="typography-h4-str text-center text-n-950">
						{{ props.pageContent.edgeCase.serverError.title }}
					</p>
					<p class="typography-s1-reg text-center text-n-400">
						{{ props.pageContent.edgeCase.serverError.description }}
					</p>
				</div>

				<div class="w-auto">
					<BaseButton variant="primary" size="medium" @click="refreshPage">
						{{ props.pageContent.edgeCase.serverError.buttonText }}
					</BaseButton>
				</div>
			</div>
		</div>
	</template>

	<template v-else-if="campaignFirstSlugData">
		<main>
			<!-- Top Banner -->
			<div
				class="w-full bg-white"
				v-if="campaignFirstSlugData.webImage && campaignFirstSlugData.appImage"
			>
				<div class="mx-auto aspect-[4/3] max-w-[1440px] mdp:aspect-[3/1]">
					<img
						:src="
							isMobile
								? campaignFirstSlugData.appImage
								: campaignFirstSlugData.webImage
						"
						:alt="campaignFirstSlugData.name"
						class="h-full w-full object-cover"
						loading="lazy"
					/>
				</div>
			</div>

			<div class="bs-container">
				<div class="col-span-12">
					<div class="flex flex-col py-[40px] mdp:py-[60px] mdl:py-[80px]">
						<div
							class="space-y-[60px] text-left mdp:space-y-[60px] mdp:text-center mdl:space-y-[80px]"
						>
							<section
								v-for="section in campaignFirstSlugData.sections"
								:key="section.menuTitle"
							>
								<!-- Coupon Area -->
								<template
									v-if="
										isSectionType(section, 'coupon') &&
										section.coupons &&
										section.coupons.length
									"
								>
									<MercariCampaignCouponSection
										:section="section"
										:coupon-card-text="couponCardText"
									/>
								</template>

								<!-- Image Area -->
								<template
									v-if="
										isSectionType(section, 'image') &&
										section.webImage &&
										section.appImage
									"
								>
									<MercariCampaignImageSection :section="section" />
								</template>

								<!-- Product List Area -->
								<template
									v-if="
										isSectionType(section, 'mercari_product_list') &&
										section.keywords &&
										section.keywords.length
									"
								>
									<MercariCampaignProductListSection
										:keywords="section.keywords"
										:title="section.title"
										:subtitle="section.subtitle"
										:hasError="hasServerError"
										:pageContent="pageContent"
										@fetch-complete="handleFetchComplete"
									/>
								</template>

								<!-- Shop ID Product List Area -->
								<template
									v-if="
										isSectionType(section, 'product_list') &&
										section.tabs &&
										section.tabs.length
									"
								>
									<MercariCampaignShopProductListSection
										:tabs="section.tabs"
										:title="section.title"
										:subtitle="section.subtitle"
										:hasError="hasServerError"
										:pageContent="pageContent"
										@fetch-complete="handleFetchComplete"
									/>
								</template>

								<!-- Blog Area -->
								<template
									v-if="
										isSectionType(section, 'blog') &&
										section.blogs &&
										section.blogs.length
									"
								>
									<div class="gap-6 md:grid-cols-2 grid">
										<MercariCampaignBlogSection
											:title="section.title"
											:section="section.blogs"
											:pageContent="pageContent"
										/>
									</div>
								</template>
								<!-- Terms Content Area -->
								<template
									v-if="isSectionType(section, 'text') && section.content"
								>
									<MercariTermsAndConditions
										:section="section"
										:pageContent="pageContent.termsAndConditions"
									/>
								</template>
							</section>
						</div>
					</div>
				</div>
			</div>
		</main>
	</template>
</template>
